// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RequestState } from "@utils/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig, ServiceBaseUrl } from "@config/config";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { InterviewStatus, SnackMessage } from "@config/constant";
import {
  AdditionalInterview,
  Interview,
  InterviewCreate,
  InterviewSkipSchedule,
  InterviewHistory,
  InterviewState,
  InterviewUpdate,
  InterviewFeedbackResponse,
  Score,
  ReminderAlertData,
  CaseStudyInterviewCreate,
} from "./model";

export const addInterviews = createAsyncThunk(
  "interview/addInterview",
  async (payload: InterviewCreate, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.interview, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addInterview,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message || SnackMessage.error.addInterview;
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const addInterviewSkipSchedule = createAsyncThunk(
  "interview/addInterviewSkipSchedule",
  async (payload: InterviewSkipSchedule, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.interview}/skip-schedule`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addInterview,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message || SnackMessage.error.addInterview;
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getCandidateInterviews = createAsyncThunk(
  "interview/getCandidateInterviews",
  async (id: number, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.candidates}/${id}/interviews`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.getInterview,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getInterviews = createAsyncThunk(
  "interview/getInterviews",
  async ({
    status,
    email,
    candidateVacancyId,
    searchString,
    limit,
    offset,
  }: {
    status: string[];
    email?: string | null;
    candidateVacancyId?: string;
    searchString?: string;
    limit: number | null;
    offset: number | null;
  }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.interview, {
          params: {
            status: status?.join(","),
            email,
            candidateVacancyId,
            limit,
            offset,
            searchString,
          },
        })
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const getInterviewById = createAsyncThunk(
  "interview/getInterviewById",
  async ({ Id }: { Id: number }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.interview}/${Id}`)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const addInterviewScore = createAsyncThunk(
  "interview/addInterviewScore",
  async ({ id, payload }: { id: number; payload: Score }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.interview}/${id}/score`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addScore,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addScore,
              type: "error",
            }),
          );
        });
    });
  },
);

export const sendFeedbackReminder = createAsyncThunk(
  "interview/sendFeedbackReminder",
  async ({ payload }: { payload: ReminderAlertData }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.feedbackReminder}`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.sendFeedbackReminder,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.sendFeedbackReminder,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateInterviewScore = createAsyncThunk(
  "interview/updateInterviewScore",
  async ({ id, payload }: { id: number; payload: Score }, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.interview}/${id}/score`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateScore,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateScore,
              type: "error",
            }),
          );
        });
    });
  },
);

export const addAdditionalInterview = createAsyncThunk(
  "interview/addAdditionalInterview",
  async (
    { id, payload }: { id: number; payload: AdditionalInterview },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.vacancies}/${id}/additional-interview`,
          payload,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addInterview,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addInterview,
              type: "error",
            }),
          );
        });
    });
  },
);

export const editInterviews = createAsyncThunk(
  "interview/editInterview",
  async (
    { id, payload }: { id: number; payload: InterviewUpdate },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.interview}/${id}`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.editInterview,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.editInterview,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteInterview = createAsyncThunk(
  "interview/deleteInterview",
  async (
    { id, additionalInfo }: { id: number; additionalInfo: string },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .delete(`${AppConfig.serviceUrls.interview}/${id}`, {
          data: { additionalInfo },
        })
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message:
                SnackMessage.success.deleteInterview ||
                "Interview deleted successfully",
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message ||
            SnackMessage.error.deleteInterview ||
            "Failed to delete interview";
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getInterviewFeedbacks = createAsyncThunk(
  "interview/getInterviewFeedbacks",
  async ({ interviewId }: { interviewId: number }, { dispatch }) => {
    return new Promise<InterviewFeedbackResponse[]>((resolve, reject) => {
      const url = `${AppConfig.serviceUrls.interview}/${interviewId}/feedbacks`;
      APIService.getInstance()
        .get(url)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.getInterviewFeedbacks,
              type: "error",
            }),
          );
        });
    });
  },
);

export const getBusyTimes = createAsyncThunk(
  "interview/getBusyTimes",
  async ({
    minTime,
    maxTime,
    users,
    timeZone,
  }: {
    minTime: string;
    maxTime: string;
    users: string[];
    timeZone: string;
  }) => {
    return new Promise<any>((resolve, reject) => {
      const baseUrl = `${ServiceBaseUrl}/busy-times`;
      const queryParams = new URLSearchParams({
        minTime,
        maxTime,
        timeZone,
      });

      users.forEach((user) => {
        queryParams.append("users", user);
      });

      const endPoint = `${baseUrl}?${queryParams.toString()}`;

      APIService.getInstance()
        .get(endPoint)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const getMeetingRooms = createAsyncThunk(
  "interview/getMeetingRoomResources",
  async () => {
    return new Promise<any>((resolve, reject) => {
      const endPoint = `${ServiceBaseUrl}/meeting-rooms`;

      APIService.getInstance()
        .get(endPoint)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
);

export const addCaseStudyInterview = createAsyncThunk(
  "interview/addCaseStudyInterview",
  async (payload: CaseStudyInterviewCreate, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.interview}/case-study`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addInterview,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message || SnackMessage.error.addInterview;
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const editCaseStudyInterviews = createAsyncThunk(
  "interview/editCaseStudyInterview",
  async (
    { groupId, payload }: { groupId: string; payload: InterviewUpdate },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(
          `${AppConfig.serviceUrls.interview}/case-study/${groupId}`,
          payload,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.editInterview,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message || SnackMessage.error.editInterview;
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteCaseStudyInterview = createAsyncThunk(
  "interview/deleteCaseStudyInterview",
  async (
    { groupId, additionalInfo }: { groupId: string; additionalInfo: string },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .delete(`${AppConfig.serviceUrls.interview}/case-study/${groupId}`, {
          data: { additionalInfo },
        })
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message:
                SnackMessage.success.deleteInterview ||
                "Case Study interview deleted successfully",
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          const errorMessage =
            error.response?.data?.message ||
            SnackMessage.error.deleteInterview ||
            "Failed to delete Case Study interview";
          dispatch(
            enqueueSnackbarMessage({
              message: errorMessage,
              type: "error",
            }),
          );
        });
    });
  },
);

const initialState: InterviewState = {
  interviewResponse: {
    interview: [],
    totalCount: 0,
  },
  selectedInterview: null,
  interviewHistory: [],
  busyTimes: [],
  interviewFeedbacksResponse: [],
  interviewFeedbacksRequestState: RequestState.IDLE,
  meetingRoomResources: [],
  state: RequestState.IDLE,
  feedbackReminderState: RequestState.IDLE,
  busyState: RequestState.IDLE,
  meetingRoomState: RequestState.IDLE,
  gettingAllInterviewsState: RequestState.IDLE,
  gettingSingleInterviewState: RequestState.IDLE,
};

const interviewSlice = createSlice({
  name: "interview",
  initialState,
  reducers: {
    resetBusyState: (state) => {
      state.busyState = RequestState.IDLE;
    },
    resetMeetingRoomState: (state) => {
      state.meetingRoomState = RequestState.IDLE;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addInterviews.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(addInterviews.fulfilled, (state) => {
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(addInterviews.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
    builder.addCase(addInterviewSkipSchedule.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(addInterviewSkipSchedule.fulfilled, (state) => {
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(addInterviewSkipSchedule.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
    builder.addCase(getCandidateInterviews.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      getCandidateInterviews.fulfilled,
      (state, action: PayloadAction<InterviewHistory[]>) => {
        state.interviewHistory = action.payload;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getCandidateInterviews.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
    builder.addCase(getInterviews.pending, (state) => {
      state.gettingAllInterviewsState = RequestState.LOADING;
    });
    builder.addCase(
      getInterviews.fulfilled,
      (
        state,
        action: PayloadAction<{ interviews: Interview[]; totalCount: number }>,
      ) => {
        state.interviewResponse.interview = action.payload.interviews;
        state.interviewResponse.totalCount = action.payload.totalCount;
        state.gettingAllInterviewsState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getInterviews.rejected, (state) => {
      state.gettingAllInterviewsState = RequestState.FAILED;
    });
    builder.addCase(getInterviewById.pending, (state) => {
      state.gettingSingleInterviewState = RequestState.LOADING;
      state.stateMessage = "Fetching interview details...";
    });
    builder.addCase(
      getInterviewById.fulfilled,
      (state, action: PayloadAction<Interview>) => {
        state.selectedInterview = action.payload;
        state.gettingSingleInterviewState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getInterviewById.rejected, (state) => {
      state.gettingSingleInterviewState = RequestState.FAILED;
    });
    builder.addCase(addInterviewScore.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      addInterviewScore.fulfilled,
      (state, action: PayloadAction<number>) => {
        state.interviewResponse.interview.map((i) => {
          if (i.id === action.payload)
            return { ...i, status: InterviewStatus.COMPLETED };
          return i;
        });
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(addInterviewScore.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
    builder.addCase(sendFeedbackReminder.pending, (state) => {
      state.feedbackReminderState = RequestState.LOADING;
      state.stateMessage = "Sending feedback reminder...";
    });
    builder.addCase(sendFeedbackReminder.fulfilled, (state) => {
      state.feedbackReminderState = RequestState.SUCCEEDED;
    });
    builder.addCase(sendFeedbackReminder.rejected, (state) => {
      state.feedbackReminderState = RequestState.FAILED;
    });
    builder.addCase(updateInterviewScore.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      updateInterviewScore.fulfilled,
      (state, action: PayloadAction<number>) => {
        state.interviewResponse.interview.map((i) => {
          if (i.id === action.payload)
            return { ...i, status: InterviewStatus.COMPLETED };
          return i;
        });
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(updateInterviewScore.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
    builder.addCase(addAdditionalInterview.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(addAdditionalInterview.fulfilled, (state) => {
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(addAdditionalInterview.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
    builder.addCase(editInterviews.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      editInterviews.fulfilled,
      (state, action: PayloadAction<Interview>) => {
        const interviewId = action.payload.id;
        state.interviewResponse.interview =
          state.interviewResponse.interview.map((i) => {
            if (i.id === interviewId) return action.payload;
            return i;
          });
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(editInterviews.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(deleteInterview.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      deleteInterview.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(deleteInterview.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getBusyTimes.pending, (state) => {
      state.busyState = RequestState.LOADING;
      state.stateMessage = "Fetching interviewer busy times...";
    });
    builder.addCase(
      getBusyTimes.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.busyTimes = action.payload;
        state.busyState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getBusyTimes.rejected, (state) => {
      state.busyState = RequestState.FAILED;
    });

    builder.addCase(getMeetingRooms.pending, (state) => {
      state.meetingRoomState = RequestState.LOADING;
      state.stateMessage = "Fetching meeting rooms...";
    });
    builder.addCase(
      getMeetingRooms.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.meetingRoomResources = action.payload;
        state.meetingRoomState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getMeetingRooms.rejected, (state) => {
      state.meetingRoomState = RequestState.FAILED;
    });
    builder.addCase(getInterviewFeedbacks.pending, (state) => {
      state.interviewFeedbacksRequestState = RequestState.LOADING;
      state.stateMessage = "Fetching interview feedbacks...";
    });
    builder.addCase(
      getInterviewFeedbacks.fulfilled,
      (state, action: PayloadAction<InterviewFeedbackResponse[]>) => {
        state.interviewFeedbacksResponse = action.payload;
        state.interviewFeedbacksRequestState = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getInterviewFeedbacks.rejected, (state) => {
      state.interviewFeedbacksRequestState = RequestState.FAILED;
    });

    builder.addCase(addCaseStudyInterview.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(addCaseStudyInterview.fulfilled, (state) => {
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(addCaseStudyInterview.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(editCaseStudyInterviews.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      editCaseStudyInterviews.fulfilled,
      (state, action: PayloadAction<Interview[]>) => {
        const updatedIds = new Set(action.payload.map((iv) => iv.id));
        state.interviewResponse.interview = [
          ...state.interviewResponse.interview.filter(
            (iv) => !updatedIds.has(iv.id),
          ),
          ...action.payload,
        ];
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(editCaseStudyInterviews.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(deleteCaseStudyInterview.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(deleteCaseStudyInterview.fulfilled, (state) => {
      state.state = RequestState.SUCCEEDED;
    });
    builder.addCase(deleteCaseStudyInterview.rejected, (state) => {
      state.state = RequestState.FAILED;
    });
  },
});

export const { resetBusyState, resetMeetingRoomState } = interviewSlice.actions;
export default interviewSlice.reducer;
